import { useState } from 'react';
import { HotelWithSelection } from '@/types/hotel-search';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Star, MapPin, ExternalLink, MessageSquare, ChevronLeft, ChevronRight,
  AlertCircle, GitCompare, Footprints, Check, Edit2, RefreshCw, X, Loader2,
  Navigation, Heart,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { competitorSupplierLabel, EXPEDIA_TAAP_TIERS } from '@/lib/hotel-competitor';
import { computeGrossPriceSummary, formatGrossPrice, multiplierLabel } from '@/lib/hotel-tax';

interface HotelCardProps {
  hotel: HotelWithSelection;
  mapIndex?: number;
  nights?: number;
  isHighlighted?: boolean;
  onOpenDetail: (hotel: HotelWithSelection) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onToggleChosen: (hotelId: string) => void;
  onToggleFavorite: (hotelId: string) => void;
  onRefreshBookingUrl?: (hotelId: string, newUrl: string) => Promise<void>;
}

function StarRating({ rating, max = 5 }: { rating: number; max?: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: max }).map((_, i) => (
        <Star
          key={i}
          className={cn('w-3 h-3', i < rating ? 'fill-amber-400 text-amber-400' : 'fill-gray-200 text-gray-200')}
        />
      ))}
    </div>
  );
}

function LocationScoreBadge({
  locationScore,
  categoryRatings,
}: {
  locationScore: number;
  categoryRatings: Array<{ label: string; score: number }> | null;
}) {
  const color =
    locationScore >= 9 ? 'bg-emerald-500' :
    locationScore >= 8 ? 'bg-teal-500' :
    locationScore >= 7 ? 'bg-yellow-500' : 'bg-orange-500';

  const badge = (
    <span className={cn(
      'inline-flex items-center gap-1 text-white text-xs font-bold px-1.5 py-0.5 rounded cursor-pointer select-none',
      color,
    )}>
      <Navigation className="w-2.5 h-2.5 flex-shrink-0" />
      {locationScore.toFixed(1)}
    </span>
  );

  if (!categoryRatings?.length) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent side="top" className="text-xs">Localização</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild onClick={e => e.stopPropagation()}>
        {badge}
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="w-52 p-3"
        onClick={e => e.stopPropagation()}
      >
        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
          Avaliações por categoria
        </p>
        <div className="space-y-1.5">
          {categoryRatings.map(cat => (
            <div key={cat.label} className="flex items-center justify-between gap-2">
              <span className="text-xs text-gray-700 truncate">{cat.label}</span>
              <span className={cn(
                'text-white text-[11px] font-bold px-1.5 py-0.5 rounded flex-shrink-0',
                cat.score >= 9 ? 'bg-emerald-500' :
                cat.score >= 8 ? 'bg-teal-500' :
                cat.score >= 7 ? 'bg-yellow-500' : 'bg-orange-500',
              )}>
                {cat.score.toFixed(1)}
              </span>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function BookingScore({ score }: { score: number | null | undefined }) {
  if (score == null) return <span className="text-xs text-gray-300 px-1.5 py-0.5">N/A</span>;
  const color =
    score >= 9 ? 'bg-emerald-500' :
    score >= 8 ? 'bg-teal-500' :
    score >= 7 ? 'bg-yellow-500' : 'bg-orange-500';
  return (
    <span className={cn('text-white text-xs font-bold px-1.5 py-0.5 rounded', color)}>
      {score.toFixed(1)}
    </span>
  );
}

export function HotelCard({
  hotel,
  mapIndex,
  nights,
  isHighlighted = false,
  onOpenDetail,
  onMouseEnter,
  onMouseLeave,
  onToggleChosen,
  onToggleFavorite,
  onRefreshBookingUrl,
}: HotelCardProps) {
  const [imgIndex, setImgIndex] = useState(0);
  const [isEditingUrl, setIsEditingUrl] = useState(false);
  const [editUrlValue, setEditUrlValue] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefreshUrl = async () => {
    if (!onRefreshBookingUrl || !editUrlValue.trim()) return;
    setIsRefreshing(true);
    try {
      await onRefreshBookingUrl(hotel.hotel_id, editUrlValue.trim());
      setIsEditingUrl(false);
      setEditUrlValue('');
    } finally {
      setIsRefreshing(false);
    }
  };

  const images = hotel.images ?? [];
  const totalImages = images.length;
  const isChosen = hotel.selection.status === 'chosen';
  const isFavorite = hotel.selection.is_favorite === true;
  const walk = hotel.walking_distance;
  const walkRef = walk?.reference?.trim();
  const walkMin = walk?.minutes;
  const showWalking =
    walk != null && (Number.isFinite(walkMin as number) || Boolean(walkRef));

  const prevImg = (e: React.MouseEvent) => {
    e.stopPropagation();
    setImgIndex(i => (i - 1 + totalImages) % totalImages);
  };

  const nextImg = (e: React.MouseEvent) => {
    e.stopPropagation();
    setImgIndex(i => (i + 1) % totalImages);
  };

  const hasComment = !!hotel.selection.comments?.trim();

  return (
    <div
      id={`hotel-card-${hotel.hotel_id}`}
      className={cn(
        'relative rounded-xl border shadow-sm select-none overflow-hidden scroll-mt-3',
        'hover:shadow-md transition-all duration-200',
        isChosen
          ? 'border-voah-teal/40 ring-1 ring-voah-teal/15'
          : isFavorite
            ? 'border-rose-200/80 ring-1 ring-rose-100/80'
            : 'border-gray-200',
        isHighlighted && 'ring-2 ring-voah-coral/80 shadow-md z-10',
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={isFavorite ? 'Remover dos favoritos' : 'Favoritar'}
            className={cn(
              'absolute top-2 right-2 z-20 flex h-8 w-8 items-center justify-center rounded-full shadow-md transition-colors',
              'bg-white/95 hover:bg-white ring-1 ring-black/5',
            )}
            onClick={e => {
              e.stopPropagation();
              onToggleFavorite(hotel.hotel_id);
            }}
          >
            <Heart
              className={cn(
                'w-4 h-4 transition-colors',
                isFavorite ? 'fill-rose-500 text-rose-500' : 'text-gray-400 hover:text-rose-400',
              )}
            />
          </button>
        </TooltipTrigger>
        <TooltipContent side="left" className="text-xs">
          {isFavorite ? 'Remover dos favoritos' : 'Favoritar'}
        </TooltipContent>
      </Tooltip>
      {/* Barra superior: escolha + indicadores */}
      <div
        className={cn(
          'flex items-center justify-between gap-2 px-3 py-2',
          isChosen ? 'bg-voah-teal text-white' : 'bg-gray-50 border-b border-gray-100',
        )}
      >
        <button
          type="button"
          className={cn(
            'text-xs font-medium rounded-md px-2 py-1 transition-colors',
            isChosen
              ? 'bg-white/20 hover:bg-white/30 text-white'
              : 'text-gray-600 hover:bg-gray-200/90',
          )}
          onClick={e => {
            e.stopPropagation();
            onToggleChosen(hotel.hotel_id);
          }}
        >
          {isChosen ? (
            <span className="flex items-center gap-1">
              <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
              Escolhido
            </span>
          ) : (
            'Escolher'
          )}
        </button>
        <div className="flex items-center gap-1.5">
          {!hotel.availability && (
            <Badge
              variant="outline"
              className={cn(
                'text-xs px-1.5 py-0 border-orange-200 bg-orange-50 text-orange-600',
                isChosen && 'border-white/40 bg-white/15 text-white',
              )}
            >
              <AlertCircle className="w-3 h-3 mr-1" />
              Indisponível
            </Badge>
          )}
          {hasComment && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className={cn(isChosen ? 'text-white' : 'text-voah-teal')}>
                  <MessageSquare className="w-3.5 h-3.5" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-xs">
                {hotel.selection.comments}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Corpo clicável: imagem + conteúdo */}
      <div
        className="bg-white cursor-pointer"
        onClick={() => onOpenDetail(hotel)}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpenDetail(hotel);
          }
        }}
      >
      {/* Image carousel */}
      <div className="relative mx-3 rounded-lg overflow-hidden bg-gray-100" style={{ height: 160 }}>
        {totalImages > 0 ? (
          <>
            <img
              src={images[imgIndex]}
              alt={hotel.name}
              className="w-full h-full object-cover"
              draggable={false}
            />
            {mapIndex !== undefined && (
              <div className="absolute top-2 left-2 w-7 h-7 rounded-full bg-voah-teal text-white text-xs font-bold flex items-center justify-center shadow-md ring-2 ring-white">
                {mapIndex}
              </div>
            )}
            {totalImages > 1 && (
              <>
                <button
                  onClick={prevImg}
                  className="absolute left-1.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-white transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={nextImg}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-white transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                {/* Dots */}
                <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
                  {images.map((_, i) => (
                    <button
                      key={i}
                      onClick={e => { e.stopPropagation(); setImgIndex(i); }}
                      className={cn(
                        'rounded-full transition-all',
                        i === imgIndex ? 'bg-white w-3 h-1.5' : 'bg-white/50 w-1.5 h-1.5',
                      )}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300">
            <MapPin className="w-8 h-8" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="px-3 pb-3 pt-2.5 space-y-2">
        {/* Name + stars */}
        <div>
          <div className="flex items-start justify-between gap-1">
            <h3 className="font-semibold text-sm text-gray-900 leading-snug line-clamp-2 flex-1">
              {hotel.name}
            </h3>
            {hotel.editorial_pick && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-0.5 rounded-md bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[10px] font-semibold flex-shrink-0">
                    <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                    Editorial
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs max-w-[220px]">
                  {hotel.editorial_pick_source?.trim() || 'Recomendação de lista ou blog'}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <StarRating rating={hotel.classification?.stars ?? 0} />
            {hotel.classification?.stars != null && (
              <span className="text-xs text-gray-400">{hotel.classification.stars} stars</span>
            )}
          </div>
          {showWalking && (
            <div className="flex items-start gap-1.5 mt-1.5 text-xs text-gray-600">
              <Footprints className="w-3.5 h-3.5 text-voah-teal flex-shrink-0 mt-0.5" />
              <div className="min-w-0 leading-snug">
                {Number.isFinite(walkMin as number) ? (
                  <span className="font-medium text-gray-800">
                    {Math.round(walkMin as number)} min a pé
                  </span>
                ) : null}
                {Number.isFinite(walkMin as number) && walkRef ? (
                  <span className="text-gray-400"> · </span>
                ) : null}
                {walkRef ? (
                  <span className={cn('text-gray-600', !Number.isFinite(walkMin as number) && 'font-medium text-gray-800')}>
                    {walkRef}
                  </span>
                ) : null}
              </div>
            </div>
          )}
        </div>

        {/* Ratings row */}
        <div className="flex items-center gap-2 flex-wrap">
          {hotel.rating && (
            <div className="flex items-center gap-1">
              <BookingScore score={hotel.rating.score} />
              {hotel.rating.label && <span className="text-xs text-gray-500">{hotel.rating.label}</span>}
              <span className="text-xs text-gray-400">({hotel.rating.review_count?.toLocaleString() ?? '—'})</span>
            </div>
          )}
          {hotel.rating?.location_score != null && (
            <LocationScoreBadge
              locationScore={hotel.rating.location_score}
              categoryRatings={hotel.rating.category_ratings ?? null}
            />
          )}
          {hotel.google_reviews && (
            <div className="flex items-center gap-1">
              <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
              <span className="text-xs font-medium text-gray-700">{hotel.google_reviews.rating}</span>
              <span className="text-xs text-gray-400">Google</span>
            </div>
          )}
        </div>

        {/* Price — total for the stay, taxes already included */}
        {(() => {
          const currency = hotel.price_summary?.currency ?? 'BRL';
          const gross = hotel.rooms?.length
            ? computeGrossPriceSummary(hotel.rooms, currency)
            : null;

          if (!gross) {
            return (
              <div className="flex items-baseline gap-1">
                <span className="text-xs text-gray-400 italic">Preço indisponível</span>
              </div>
            );
          }

          const taxDisplay = gross.hasTaxes ? Math.round(gross.taxMin) : null;

          return (
            <div className="flex items-baseline gap-1 flex-wrap">
              <span className="text-xs text-gray-400">total</span>
              <span className="text-sm font-bold text-gray-900">
                {currency} {formatGrossPrice(gross.min)}
              </span>
              {Math.round(gross.min) !== Math.round(gross.max) && (
                <span className="text-xs text-gray-400">
                  — {formatGrossPrice(gross.max)}
                </span>
              )}
              {nights && nights > 0 && (
                <span className="text-xs text-gray-400">· {nights} noites</span>
              )}
              {taxDisplay != null && taxDisplay > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-xs text-orange-500 font-medium cursor-default">
                      ({currency} {formatGrossPrice(taxDisplay)} impostos já incluídos)
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs max-w-[200px] text-center">
                    Impostos já incluídos. Calculados de forma composta ({multiplierLabel(gross.multiplier)}).
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          );
        })()}

        {(() => {
          const quotes = (hotel.selection.competitor_quote ?? []).filter(cq => {
            const priceT = cq.price?.trim() ?? '';
            const descT = cq.description?.trim() ?? '';
            return !!(cq.supplier || priceT || descT);
          });
          if (quotes.length === 0) return null;

          return (
            <div className="space-y-1.5">
              {quotes.map((cq, i) => {
                const priceT = cq.price?.trim() ?? '';
                const descT = cq.description?.trim() ?? '';
                const supplierUrl = cq.supplier_url?.trim() ?? '';
                return (
                  <div
                    key={`${cq.supplier}-${i}`}
                    className="flex items-start gap-1.5 rounded-lg bg-amber-50/90 border border-amber-100 px-2 py-1.5"
                  >
                    <GitCompare className="w-3.5 h-3.5 text-amber-700 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {cq.supplier ? (
                          <p className="text-[10px] font-semibold text-amber-800 uppercase tracking-wide">
                            {competitorSupplierLabel(cq.supplier)}
                          </p>
                        ) : (
                          <p className="text-[10px] font-semibold text-amber-800 uppercase tracking-wide">Fornecedor</p>
                        )}
                        {cq.supplier === 'expedia_taap' && cq.remuneration_tier && (() => {
                          const tierLabel = EXPEDIA_TAAP_TIERS.find(t => t.value === cq.remuneration_tier)?.label ?? cq.remuneration_tier;
                          const isPremium = cq.remuneration_tier === 'premium' || cq.remuneration_tier === 'premium_plus';
                          return (
                            <span className={cn(
                              'text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full leading-none',
                              isPremium
                                ? 'bg-blue-600 text-white'
                                : 'bg-blue-100 text-blue-700 border border-blue-200',
                            )}>
                              {tierLabel}
                            </span>
                          );
                        })()}
                      </div>
                      {priceT ? (
                        supplierUrl ? (
                          <a
                            href={supplierUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-amber-950 font-medium leading-snug break-words underline decoration-amber-400/80 hover:text-amber-800 hover:decoration-amber-600"
                            onClick={e => e.stopPropagation()}
                          >
                            {priceT}
                            <ExternalLink className="w-3 h-3 flex-shrink-0 opacity-70" />
                          </a>
                        ) : (
                          <p className="text-xs text-amber-950 font-medium leading-snug break-words">{priceT}</p>
                        )
                      ) : supplierUrl ? (
                        <a
                          href={supplierUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-amber-900 font-medium underline hover:text-amber-800"
                          onClick={e => e.stopPropagation()}
                        >
                          Ver no fornecedor
                          <ExternalLink className="w-3 h-3 flex-shrink-0 opacity-70" />
                        </a>
                      ) : null}
                      {descT ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <p className="text-xs text-amber-900/85 leading-snug line-clamp-2 break-words text-center cursor-default">
                              {descT}
                            </p>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs text-xs whitespace-pre-wrap text-center">
                            {descT}
                          </TooltipContent>
                        </Tooltip>
                      ) : null}
                      {!priceT && !descT ? (
                        <p className="text-xs text-amber-950/80">—</p>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Keywords */}
        <div className="flex flex-wrap gap-1">
          {hotel.keywords.slice(0, 3).map(kw => (
            <Badge
              key={kw}
              variant="secondary"
              className="text-xs px-1.5 py-0 bg-gray-100 text-gray-600 hover:bg-gray-200 rounded-md"
            >
              {kw}
            </Badge>
          ))}
          {hotel.keywords.length > 3 && (
            <Badge variant="secondary" className="text-xs px-1.5 py-0 bg-gray-100 text-gray-500 rounded-md">
              +{hotel.keywords.length - 3}
            </Badge>
          )}
        </div>

        {/* External links */}
        <div className="space-y-1.5 pt-0.5" onClick={e => e.stopPropagation()}>
          {!isEditingUrl ? (
            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    href={hotel.booking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    <ExternalLink className="w-3 h-3" />
                    <span>Booking</span>
                  </a>
                </TooltipTrigger>
                <TooltipContent>Abrir no Booking.com</TooltipContent>
              </Tooltip>

              {onRefreshBookingUrl && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => { setEditUrlValue(hotel.booking_url); setIsEditingUrl(true); }}
                      className="text-gray-300 hover:text-blue-500 transition-colors"
                    >
                      <Edit2 className="w-3 h-3" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Editar URL do Booking</TooltipContent>
                </Tooltip>
              )}

              <span className="text-gray-200">·</span>

              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    href={hotel.google_maps_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-voah-teal hover:text-voah-teal-dark transition-colors"
                  >
                    <MapPin className="w-3 h-3" />
                    <span>Maps</span>
                  </a>
                </TooltipTrigger>
                <TooltipContent>Abrir no Google Maps</TooltipContent>
              </Tooltip>
            </div>
          ) : (
            <div className="space-y-1.5">
              <div className="flex items-center gap-1">
                <input
                  type="url"
                  value={editUrlValue}
                  onChange={e => setEditUrlValue(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleRefreshUrl();
                    if (e.key === 'Escape') { setIsEditingUrl(false); setEditUrlValue(''); }
                  }}
                  placeholder="https://www.booking.com/hotel/..."
                  className="flex-1 min-w-0 text-xs border border-blue-300 rounded-md px-2 py-1 outline-none focus:ring-1 focus:ring-blue-400 disabled:opacity-60"
                  disabled={isRefreshing}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={handleRefreshUrl}
                      disabled={isRefreshing || !editUrlValue.trim()}
                      className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-md bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 transition-colors"
                    >
                      {isRefreshing
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <RefreshCw className="w-3.5 h-3.5" />
                      }
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Re-enriquecer com nova URL</TooltipContent>
                </Tooltip>
                <button
                  type="button"
                  onClick={() => { setIsEditingUrl(false); setEditUrlValue(''); }}
                  disabled={isRefreshing}
                  className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    href={hotel.google_maps_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-voah-teal hover:text-voah-teal-dark transition-colors"
                  >
                    <MapPin className="w-3 h-3" />
                    <span>Maps</span>
                  </a>
                </TooltipTrigger>
                <TooltipContent>Abrir no Google Maps</TooltipContent>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
